import { Dropdown, Checkbox, GridColumn, Grid, Input } from 'semantic-ui-react';
import { useEffect, useState } from 'react';
import { useUserContext } from '~/hooks';
import { ProjectStatusFiltersList } from '~/constants';
import { useAdminUsersQuery } from '~/requests/admin/useAdminUsersQuery';
import { ProjectFiltersType } from '~/types/project/ProjectFiltersType';

interface ProjectFiltersProps {
  setActiveFilters: (filters: any) => void;
}

export default function ProjectFilters(props: ProjectFiltersProps) {

  const { loggedInUser } = useUserContext();
  const { setActiveFilters } = props;

  const { data: users } = useAdminUsersQuery();

  const statusOptions = ProjectStatusFiltersList.filter(({ hideForInvestors }) => {
    return !(loggedInUser.isInvestor && !loggedInUser.isInstaller && hideForInvestors);
  }).map(({ value, label }) => ({
    key: value,
    value: value,
    text: label,
  }));

  const ownerOptions = users?.map((user) => ({
    key: user.id,
    text: user.name,
    value: user.id,
  })) || [];

  const pricingTypeOptions = [
    { key: 'offtake-ppa', text: 'Offtake', value: 'OFFTAKE' },
    { key: 'acquisition', text: 'Acquisition', value: 'ACQUISITION' },
    { key: 'itc', text: 'ITC', value: 'ITC' },
    { key: 'dev-fee', text: 'Development Fee', value: 'DEV_FEE' },
  ];

  const deploymentTypeOptions = [
    { key: 'ci', text: 'Commercial & Industrial', value: 'CI' },
    { key: 'community-solar', text: 'Community Solar', value: 'COMMUNITY_SOLAR' },
    { key: 'utility', text: 'Utility', value: 'UTILITY' },
  ];

  const developmentStageOptions = [
    { key: 'prospecting', text: 'Prospecting', value: 'PROSPECTING' },
    { key: 'development', text: 'Development', value: 'DEVELOPMENT' },
    { key: 'ntp', text: 'At or near NTP', value: 'NTP' },
    { key: 'operating', text: 'Operating', value: 'OPERATING' },
    { key: 'construction', text: 'Construction', value: 'CONSTRUCTION' },
  ];

  const colStyle = { padding: '1rem 0 0 1rem' };

  const [filters, setFilters] = useState<ProjectFiltersType>({
    selectedStatuses: [],
    selectedOwners: [],
    selectedPricingType: '',
    selectedDeploymentType: '',
    selectedDevelopmentStage: '',
    developerName: '',
    investorName: '',
    hasOpenItems: false,
    includeArchived: false,
    state: ''
  });

  const updateFilters = (_: any, data: any) => {
    const { name, value, checked } = data;

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value !== undefined ? value : checked,
    }));
  };

  useEffect(() => {
    setActiveFilters(filters);
  }, [filters]);

  return (
    <div style={{ margin: '1rem 0 2rem', paddingRight: '1rem' }}>
      <Grid stackable>
        {loggedInUser.isAdmin && (
          <GridColumn mobile={16} tablet={6} computer={2} style={colStyle}>
            <Input
              type="text"
              name="developerName"
              placeholder="Developer name..."
              value={filters.developerName}
              onChange={updateFilters}
              fluid
              style={{ fontSize: '.9rem' }}
            />
          </GridColumn>
        )}
        {loggedInUser.isAdmin && (
          <GridColumn mobile={16} tablet={6} computer={2} style={colStyle}>
            <Input
              type="text"
              name="investorName"
              placeholder="Investor name..."
              value={filters.investorName}
              onChange={updateFilters}
              fluid
              style={{ fontSize: '.9rem' }}
            />
          </GridColumn>
        )}
        {loggedInUser.isAdmin && (
          <GridColumn mobile={16} tablet={6} computer={2} style={colStyle}>
            <Dropdown
              name="selectedPricingType"
              placeholder="Bid type..."
              fluid
              search
              selection
              clearable
              options={pricingTypeOptions}
              value={filters.selectedPricingType || ''}
              onChange={updateFilters}
              style={{ fontSize: '.9rem' }}
              selectOnBlur={false}
            />
          </GridColumn>
        )}
        {(loggedInUser.isAdmin || loggedInUser.isInvestor) && (
          <GridColumn mobile={16} tablet={6} computer={2} style={colStyle}>
            <Dropdown
              name="selectedDeploymentType"
              placeholder="Revenue type..."
              fluid
              search
              selection
              clearable
              options={deploymentTypeOptions}
              value={filters.selectedDeploymentType || ''}
              onChange={updateFilters}
              style={{ fontSize: '.9rem' }}
              selectOnBlur={false}
            />
          </GridColumn>
        )}
        {(loggedInUser.isAdmin) && (
          <GridColumn mobile={16} tablet={6} computer={2} style={colStyle}>
            <Dropdown
              name="selectedDevelopmentStage"
              placeholder="Dev stage..."
              fluid
              search
              selection
              clearable
              options={developmentStageOptions}
              value={filters.selectedDevelopmentStage || ''}
              onChange={updateFilters}
              style={{ fontSize: '.9rem' }}
              selectOnBlur={false}
            />
          </GridColumn>
        )}
        <GridColumn mobile={16} tablet={6} computer={3} style={colStyle}>
          <Dropdown
            name="selectedStatuses"
            placeholder="Status..."
            fluid
            multiple
            search
            selection
            options={statusOptions}
            value={filters.selectedStatuses}
            onChange={updateFilters}
            style={{ fontSize: '.9rem' }}
            size="small"
            clearable
            selectOnBlur={false}
          />
        </GridColumn>
        {loggedInUser.isAdmin && (
          <GridColumn mobile={16} tablet={6} computer={3} style={colStyle}>
            <Dropdown
              name="selectedOwners"
              placeholder="Owner..."
              fluid
              multiple
              search
              selection
              clearable
              options={ownerOptions}
              value={filters.selectedOwners || ''}
              onChange={updateFilters}
              style={{ fontSize: '.9rem' }}
              selectOnBlur={false}
            />
          </GridColumn>
        )}
        {loggedInUser.isAdmin && (
          <GridColumn mobile={16} tablet={3} computer={2} style={colStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '.5rem', marginTop: '.5rem' }}>
              <Checkbox
                name="hasOpenItems"
                label=""
                toggle
                checked={filters.hasOpenItems}
                onChange={updateFilters}
              />
              <span style={{ fontSize: '.9rem', marginLeft: '-.2rem' }}>Open Items</span>
            </div>

          </GridColumn>
        )}
        <GridColumn mobile={16} tablet={3} computer={3} style={colStyle}>
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '.5rem', marginTop: '.5rem' }}>
            <Checkbox
              name="includeArchived"
              label=""
              toggle
              checked={filters.includeArchived}
              onChange={updateFilters}
            />
            <span style={{ fontSize: '.9rem', marginLeft: '-.2rem' }}>Include archived</span>
          </div>
        </GridColumn>

      </Grid>

    </div>
  );
}